/* eslint-disable import/no-extraneous-dependencies */
/* istanbul ignore file */
import React, { useMemo, useState } from 'react';
import { Journey, Step } from '@americanexpress/axp-apply-journey';
import { FormattedMessage } from 'react-intl';
import { useReactlytics } from '@americanexpress/reactlytics';
import PropTypes from 'prop-types';
import {
  IconTime,
  dlsBrightBlue,
} from '@americanexpress/dls-react';
import { HookFormField } from '@americanexpress/axp-acquisition-components-hook-form';
import { AuthN, Timeout, prospectFetch } from '@americanexpress/axp-acquisition-components-common';
import Eligibility from './Journey/Eligibility';
import ProductBYB from './Journey/ProductBYB';
import Submit from './Journey/Submission/Submit';
import { useProductContext } from './Product';
import { useErrorContext } from './Common/ErrorPageProvider';
import { CARD_MEMBER, CARD_SELECTOR, THANK_YOU } from '../utils/constants';
import LoadModuleInLazy from './Common/LoadModuleInLazy';
import Header from './Common/Header';
import getBrowserInfo from '../DataConversion/getBrowserInfo';
import createAnalyticsData from '../utils/createAnalyticsData';

export const errorMessage = (errorId) => <FormattedMessage id={errorId} />;
export const timeOutContent = (time) => (
  <div className="dls-gray-06">
    <div>
      <IconTime size="md" theme={dlsBrightBlue} />
    </div>
    <div id="timeout-modal-header" className="pad-1-tb">
      <FormattedMessage id="timeout.header" defaultMessage="timeout.header" />
    </div>
    <div id="timeout-modal-header" className="pad-1-tb">
      <p className="font-weight-normal">
        <FormattedMessage id="timeout.description1" defaultMessage="timeout.description1" />
      </p>
    </div>
    <div>
      <p className="font-weight-normal">
        <FormattedMessage id="timeout.description" defaultMessage="timeout.description" />
      </p>
    </div>
    <div className="heading-4 pad-1-tb">{time}</div>
  </div>
);

// eslint-disable-next-line complexity
export const getIsPostWithIdent = (applicants) => {
  const type = applicants && applicants.length > 0 && 'NONPRIMARY';
  const refId = applicants
    && applicants.length > 0
    && applicants[0]?.providersInformation?.[0]?.reference_id;
  const suppFirstName = applicants
    && applicants.length > 0
    && applicants[0]?.names?.[0]?.name?.first;
  const suppLastName = applicants
    && applicants.length > 0
    && applicants[0]?.names?.[0]?.name?.last;
  return !!(type === 'NONPRIMARY' && refId && suppFirstName && suppLastName);
};

function GermanyJourney({ host }) {
  const [showBanner, setShowBanner] = useState(false);
  const [showBannerLink, setShowBannerLink] = useState(true);
  const [metaData, setMetaData] = useState({});
  const {
    product,
    product: { account_key: accountKey }, eligibilityData,
  } = useProductContext();
  const { setAnalyticsData, handleError } = useErrorContext();
  const [submitValues, setSubmitValues] = useState({});
  const { track } = useReactlytics();
  const AuthErrorHandler = () => {
    handleError({
      logData: { message: 'authentication error' },
    });
  };

  const analyticsData = useMemo(() => {
    let data = {};
    if (product.businessCodes) {
      data = { product };
      setAnalyticsData(data);
    }
    return data;
  }, [setAnalyticsData, product]);
  const device = getBrowserInfo();

  return (
    <HookFormField.Provider value={{ error: errorMessage }}>
      <Timeout
        timeoutDelay={18}
        warningDelay={120}
        maxWidth={device.deviceType === 'M' ? '23rem' : '30rem'}
        onContinue={() => prospectFetch(`${host}/api/acquisition/digital/v1/application/session`, { method: 'POST' })}
        onLeave={() => handleError({ logData: { message: 'Timeout', analyticTag: 'timeout' } })}
        dialogHeader={timeOutContent}
        continueLabel={<FormattedMessage id="timeout.continue" defaultMessage="timeout.continue" />}
        leaveLabel={<FormattedMessage id="timeout.exit" defaultMessage="timeout.exit" />}
        showDefaultTimer={false}
      />
      {showBanner && <div className="fluid dls-deep-blue-bg" role="region"><Header showBannerLink={showBannerLink} /></div>}
      <div role="main">
        <Journey
          start="Eligibility"
          onNext={() => { document.body.scrollIntoView({ behavior: 'smooth' }); }}
          analyticsData={analyticsData}
        >
          <Step
            name="Eligibility"
            render={({ next }) => <Eligibility next={next} />}
          />
          <Step
            name="CardSelection"
            onRender={async () => {
              if (eligibilityData.eligibleCards.length > 1) {
                await track(createAnalyticsData({
                  element: 'CardSelection',
                }));
              }
            }}
            render={({ next }) => (
              <LoadModuleInLazy
                next={next}
                componentName={CARD_SELECTOR}
              />
            )}
          />
          <Step
            name="ProductBYB"
            onRender={() => {
              setShowBanner(true);
              setShowBannerLink(false);
              track(createAnalyticsData({
                element: 'BYB',
                product,
              }));
            }}
            render={({ next }) => <ProductBYB next={next} />}
          />
          <Step
            name="CardMember"
            onRender={() => {
              setShowBanner(true);
              setShowBannerLink(true);
              track(createAnalyticsData({
                element: 'Application',
                detail: ' ',
                product,
                meta: {
                  conversionStep: 'suppApplicationStart',
                  appConfig: 'addSupp:single, suppPrefil:partial, suppBU:consumer, suppType:charge',
                },
              }));
            }}
            render={({ next }) => (
              <LoadModuleInLazy
                next={next}
                submitValues={submitValues}
                setSubmitValues={setSubmitValues}
                componentName={CARD_MEMBER}
              />
            )}
          />
          <Step
            name="Authorization"
            element="Authentication"
            render={({ next }) => (
              <AuthN
                onSuccess={next}
                onInterrupt={AuthErrorHandler}
                onError={AuthErrorHandler}
                options={{
                  accountKey,
                  actionId: 'DEVC01',
                  applicationId: 'DECA01',
                }}
              />
            )}
          />
          <Step
            name="Submit"
            render={({ next }) => (
              <Submit
                next={next}
                submitValues={submitValues}
                setMetaData={setMetaData}
                analyticsData={analyticsData}
                track={track}
              />
            )}
          />
          <Step
            name="ThankYou"
            element="ThankYou"
            onRender={() => {
              setShowBanner(true);
              setShowBannerLink(false);
              let decision = metaData.applicants
                && metaData.applicants.length > 0
                && metaData.applicants[0].decision;
              decision = decision.slice(0, 1).toUpperCase() + decision.slice(1).toLowerCase();
              track(createAnalyticsData({
                element: 'ThankYou',
                detail: ' ',
                product,
                meta: {
                  conversionStep: 'suppApplicationComplete',
                  pcn: metaData.pcn,
                  appDecision: decision,
                  appConfig: `addSupp:single, suppPrefil:partial, suppBU:consumer, suppType:charge, Pended-PostIdent:${getIsPostWithIdent(metaData.applicants) ? 'Yes' : 'No'}`,
                },
              }));
            }}
            render={({ previous }) => (
              <LoadModuleInLazy
                previous={previous}
                metaData={metaData}
                componentName={THANK_YOU}
                submitValues={submitValues}
                isPostWithIdent={getIsPostWithIdent(metaData.applicants)}
              />
            )}
          />
        </Journey>
      </div>
    </HookFormField.Provider>
  );
}

GermanyJourney.propTypes = {
  host: PropTypes.string.isRequired,
};
export default GermanyJourney;
